import { Line } from 'react-chartjs-2';
import { Chart, Plugin } from 'chart.js';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  CategoryScale,
} from 'chart.js';
import styles from './UserActivityChart.module.scss';
import dayjs from 'dayjs';
import ArrowLeft from '~/components/Dashboard/images/arrow-left.svg?react';
import ArrowRight from '~/components/Dashboard/images/arrow-right.svg?react';
import { useRef } from 'react';
import {
    getDatasets,
  getOptions,
  MetaEntry,
} from './UserLineChartOptions';
import './UserActivityChart.scss';

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  CategoryScale
);

export interface IUserLoginStats {
  ['1_day']: number;
  ['7_day']: number;
  ['30_day']: number;
  login_count: number;
}

export interface IUserLoginData {
  [date: string]: IUserLoginStats | { login_count: number };
}

type UserLoginDataEntry = [userId: string, data: IUserLoginData];

interface UserLineActivityChartProps {
  isUsersLoginDataLoading: boolean;
  data: UserLoginDataEntry[];
  hasFilters: boolean;
  formValues: {
    condition: { display_name: string; id: number; name: string };
    id: string;
    value: string;
  }[];
  handleWeekChange: (trigger: 'prev' | 'next') => void;
  isPrevDisabled?: boolean;
  isNextDisabled?: boolean;
}

const formattedData = (data: UserLoginDataEntry[], hasFilters: boolean) => {
  const dates = data.map(item => item[0]);

  const newData = data.map(item => {
    return {
      date: item[0],
      loginData: item[1],
    };
  });

  const loginCounts = hasFilters
    ? newData.map(item => {
        const keyValue = Object.keys(item.loginData);
        const uniqueValue = keyValue[0];
        return uniqueValue;
      })
    : [];

  const monthes = dates
    .map(item => dayjs(item).format('YYYY-MM'))
    .filter((item, idx, arr) => {
      return arr.indexOf(item) === idx;
    });

  const filteredDataByMonth = monthes.map(item => {
    return {
      month: item,
      data: newData.filter(el => el.date.includes(item)),
    };
  });

  const uniqueLoginCounts = new Set(loginCounts);

  return { filteredDataByMonth, uniqueLoginCounts, newData, monthes };
};

const verticalLinePlugin: Plugin<'line'> = {
  id: 'verticalLine',
  afterDraw(chart: Chart<'line'>) {
    const activeElements = chart.tooltip?.getActiveElements?.();

    if (activeElements && activeElements.length > 0) {
      const ctx = chart.ctx;
      const x = activeElements[0].element.x;
      const topY = chart.scales.y.top;
      const bottomY = chart.scales.y.bottom;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#9CA3AF';
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.restore();
    }
  },
};

export const UserLineActivityChart: React.FC<UserLineActivityChartProps> = ({
  isUsersLoginDataLoading,
  data,
  hasFilters,
  formValues,
  handleWeekChange,
  isPrevDisabled,
  isNextDisabled,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const newData = formattedData(data, hasFilters);
 
  const alternateDatasets=getDatasets(newData.newData, hasFilters ? formValues.map(v => v.condition.name.concat(v.value)) : null)
  const metaData: MetaEntry[][] = alternateDatasets.map(d => d.meta ?? []);

  const dataLine={
    labels: data.map(item=>item[0]),
    datasets: alternateDatasets,
  }

  const options = getOptions(tooltipRef, metaData);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.btnWrapper}>
        <button
          type="button"
          name="minus"
          onClick={() => handleWeekChange('prev')}
          className={styles.prevBtn}
          disabled={isUsersLoginDataLoading || !!isPrevDisabled}
        >
          <ArrowLeft />
        </button>
        <button
          type="button"
          name="plus"
          onClick={() => handleWeekChange('next')}
          className={styles.nextBtn}
          disabled={isUsersLoginDataLoading || !!isNextDisabled}
        >
          <ArrowRight />
        </button>
      </div>
      <div className={styles.chartWrapper}>
       {isUsersLoginDataLoading ? (
          <div className={styles.skeletonWrapper}>
            <div className={styles.skeletonLine} style={{ width: '90%', animationDelay: '0s' }} />
            <div className={styles.skeletonLine} style={{ width: '70%', animationDelay: '0.1s' }} />
            <div className={styles.skeletonLine} style={{ width: '80%', animationDelay: '0.2s' }} />
            <div className={styles.skeletonLine} style={{ width: '60%', animationDelay: '0.3s' }} />
            <div className={styles.skeletonLine} style={{ width: '75%', animationDelay: '0.4s' }} />
          </div>
        ) : (
          <Line
            data={dataLine}
            options={options}
            plugins={[verticalLinePlugin]}
          />
        )}
        <div ref={tooltipRef} className={styles.tooltipContainer} />
      </div>
    </div>
  );
};
